// Motorul de filtre-preset — inima screener-ului.
//
// Un preset are 8 condiții independente, fiecare cu un comutator și o pereche min/max.
// O condiție oprită nu filtrează nimic. Un `min`/`max` gol e ignorat, deci se poate pune
// doar limita de jos sau doar cea de sus.

/** Cele 8 condiții, în ordinea din modal. */
export const CONDITIONS = [
  {
    id: 'volume', label: 'Объём', field: 'quoteVolume', unit: '$',
    hint: 'Оборот за 24 часа в долларах. Отсекает неликвид, на котором нельзя работать.',
  },
  {
    id: 'change', label: 'Изменение цены', field: 'change24h', unit: '%',
    hint: 'Изменение цены за 24 часа. Отрицательные значения — для лидеров падения.',
  },
  {
    id: 'volatility', label: 'Волатильность', field: 'volatility24h', unit: '%',
    hint: 'Размах между максимумом и минимумом суток в процентах. Чем выше — тем больше ход.',
  },
  {
    id: 'trades', label: 'Сделки', field: 'trades', unit: '',
    hint: 'Количество сделок за 24 часа. Показывает, есть ли реальная активность.',
  },
  {
    id: 'correlation', label: 'Корреляция с BTC', field: 'corr24', unit: '',
    hint: 'От −1 до 1. Около нуля — монета ходит сама по себе, независимо от биткоина.',
  },
  {
    id: 'volumeSpike', label: 'Всплески объёма', field: 'volTrend', unit: '%',
    hint: 'Насколько текущий оборот выше обычного. 100% — вдвое больше нормы.',
  },
  {
    id: 'funding', label: 'Фандинг', field: 'funding', unit: '%',
    hint: 'Ставка финансирования. Сильный перекос — признак перегретых позиций.',
  },
  {
    id: 'openInterest', label: 'Открытый интерес', field: 'openInterest', unit: '$',
    hint: 'Сколько денег стоит в открытых позициях по монете.',
  },
];

const BY_ID = new Map(CONDITIONS.map((c) => [c.id, c]));

/** Tipurile de sortare, exact ca în modalul ScalpX. */
export const SORT_TYPES = [
  { id: 'top_growth', label: 'По лидерам роста', field: 'change24h', dir: -1 },
  { id: 'top_fall', label: 'По лидерам падения', field: 'change24h', dir: 1 },
  { id: 'volume', label: 'По объёму', field: 'quoteVolume', dir: -1 },
  { id: 'volatility', label: 'По волатильности', field: 'volatility24h', dir: -1 },
  { id: 'change', label: 'По изменению цены', field: 'change24h', dir: -1 },
  { id: 'trades', label: 'По количеству сделок', field: 'trades', dir: -1 },
  { id: 'natr', label: 'По NATR', field: 'natr', dir: -1 },
  { id: 'volume_spike', label: 'По всплеску объёма', field: 'volTrend', dir: -1 },
];

const SORT_BY_ID = new Map(SORT_TYPES.map((s) => [s.id, s]));

/** Iconițele oferite la crearea unui filtru. */
export const FILTER_ICONS = [
  'fa-chart-column', 'fa-arrow-trend-up', 'fa-arrow-trend-down', 'fa-arrows-up-down',
  'fa-fire', 'fa-bolt', 'fa-star', 'fa-crosshairs', 'fa-wave-square', 'fa-gem',
  'fa-rocket', 'fa-magnet',
];

/** Preset gol pentru „Создать фильтр". */
export function blankPreset() {
  const preset = {
    id: `f_${Date.now().toString(36)}`,
    name: '',
    icon: 'fa-chart-column',
    market: 'futures',
    chartTf: '5m',
    maxCoins: 40,
    sortFreq: '1',
    sortType: 'top_growth',
    blacklist: [],
    formation: 'none',
    pinAlerts: false,
    themeAccent: 'purple',
  };
  for (const c of CONDITIONS) {
    preset[`${c.id}Enabled`] = false;
    preset[`${c.id}Min`] = null;
    preset[`${c.id}Max`] = null;
  }
  return preset;
}

function num(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Trece rândul de filtrul dat? */
export function matches(preset, row) {
  if (preset.blacklist?.length && preset.blacklist.includes(row.symbol)) return false;

  for (const cond of CONDITIONS) {
    if (!preset[`${cond.id}Enabled`]) continue;
    const value = num(row[cond.field]);
    if (value === null) return false;               // metrică lipsă => nu trece
    const min = num(preset[`${cond.id}Min`]);
    const max = num(preset[`${cond.id}Max`]);
    if (min !== null && value < min) return false;
    if (max !== null && value > max) return false;
  }

  // Filtrul de formație folosește semnalele motorului nostru de pattern-uri.
  if (preset.formation && preset.formation !== 'none') {
    if (row.formation !== preset.formation) return false;
  }
  return true;
}

/** Comparator pentru un tip de sortare. */
export function comparator(sortType) {
  const sort = SORT_BY_ID.get(sortType) || SORT_BY_ID.get('top_growth');
  return (a, b) => {
    const av = num(a[sort.field]) ?? -Infinity;
    const bv = num(b[sort.field]) ?? -Infinity;
    if (av === bv) return a.symbol.localeCompare(b.symbol);
    return sort.dir < 0 ? bv - av : av - bv;
  };
}

/**
 * Aplică filtrul: întoarce toate potrivirile (sortate) plus felia limitată la `maxCoins`.
 * Preview-ul din modal are nevoie de ambele — „300 монет проходят фильтр,
 * в сетке покажем первые 40".
 */
export function apply(preset, rows) {
  const all = rows.filter((row) => matches(preset, row)).sort(comparator(preset.sortType));
  const limit = Number(preset.maxCoins) || all.length;
  return { total: all.length, all, visible: all.slice(0, limit) };
}

/** Filtrul implicit „Все монеты" — fără condiții, sortat pe volum. */
export const ALL_COINS = {
  id: 'all',
  name: 'Все монеты',
  icon: 'fa-layer-group',
  market: 'futures',
  chartTf: '5m',
  maxCoins: 300,
  sortType: 'volume',
  blacklist: [],
  formation: 'none',
};

/** Pseudo-filtrul „Мои закладки" — sursa e lista de bookmark-uri, nu condițiile. */
export const BOOKMARKS = {
  id: 'bookmarks',
  name: 'Мои закладки',
  icon: 'fa-bookmark',
  market: 'futures',
  chartTf: '5m',
  maxCoins: 300,
  sortType: 'volume',
  blacklist: [],
  formation: 'none',
};

export function conditionById(id) {
  return BY_ID.get(id);
}

export function sortLabel(id) {
  return SORT_BY_ID.get(id)?.label || id;
}
